import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Briefcase, Loader2, Truck, Users, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface Project {
  id: string; code: string; name_en: string; name_ar: string | null;
  description: string | null; client_name: string | null; primary_city: string | null;
  status: string; start_date: string | null; end_date: string | null;
  uses_fixed_sites: boolean; notes: string | null;
  vehicle_count: number; driver_count: number;
}
interface Vehicle { id: string; code: string; plate_en: string; vehicle_type: string; status: string; brand: string | null; model: string | null }
interface Driver  { id: string; code: string; full_name_en: string; phone: string; status: string }

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [project, setProject] = useState<Project | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [tab, setTab] = useState<'overview' | 'vehicles' | 'drivers'>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    Promise.all([
      apiClient.get<{ project: Project }>('/api/v1/projects/' + id),
      apiClient.get<Vehicle[]>('/api/v1/projects/' + id + '/vehicles'),
      apiClient.get<Driver[]>('/api/v1/projects/' + id + '/drivers'),
    ])
      .then(([p, v, d]) => { setProject(p.data.project); setVehicles(v.data); setDrivers(d.data); })
      .catch((e) => setError(extractErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (error || !project) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">{error ?? 'Not found'}</div>;

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard/projects"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Projects</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Briefcase className="h-4 w-4" /> {project.code}</div>
          <div className="w-12" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-4 space-y-4">
        <div className="rounded-lg border border-border p-4">
          <div className="text-xl font-medium">{project.name_en}</div>
          {project.name_ar && <div className="text-sm text-muted-foreground" dir="rtl">{project.name_ar}</div>}
          {project.primary_city && (
            <div className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" /> {project.primary_city}</div>
          )}
          {project.description && <div className="text-sm text-foreground mt-2">{project.description}</div>}
        </div>

        <div className="flex gap-2 border-b border-border">
          {([
            { v: 'overview', l: 'Overview' },
            { v: 'vehicles', l: `Vehicles (${project.vehicle_count})` },
            { v: 'drivers',  l: `Drivers (${project.driver_count})` },
          ] as const).map((x) => (
            <button key={x.v} onClick={() => setTab(x.v)}
              className={'px-3 py-2 text-sm border-b-2 -mb-px ' + (tab === x.v ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')}>
              {x.l}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card label="Client name" value={project.client_name ?? '—'} />
            <Card label="Status" value={project.status} />
            <Card label="Start date" value={project.start_date ?? '—'} />
            <Card label="End date" value={project.end_date ?? '—'} />
            <Card label="Fixed sites" value={project.uses_fixed_sites ? 'Yes' : 'No'} />
            <Card label="Notes" value={project.notes ?? '—'} />
          </div>
        )}

        {tab === 'vehicles' && (
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="text-xs text-muted-foreground bg-muted/30 border-b border-border">
                <tr><th className="text-left px-3 py-2">Code</th><th className="text-left px-3 py-2">Plate</th><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Brand / Model</th><th className="text-left px-3 py-2">Status</th></tr>
              </thead>
              <tbody>
                {vehicles.map((v) => (
                  <tr key={v.id} className="border-b border-border last:border-b-0">
                    <td className="px-3 py-2 font-mono text-xs">{v.code}</td>
                    <td className="px-3 py-2 font-mono">{v.plate_en}</td>
                    <td className="px-3 py-2 text-xs">{v.vehicle_type.replace(/_/g, ' ')}</td>
                    <td className="px-3 py-2 text-xs">{[v.brand, v.model].filter(Boolean).join(' ') || '—'}</td>
                    <td className="px-3 py-2 text-xs">{v.status}</td>
                  </tr>
                ))}
                {vehicles.length === 0 && <tr><td colSpan={5} className="text-center text-sm text-muted-foreground py-4">{t('projects.noVehicles', { defaultValue: 'No vehicles in this project.' })}</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'drivers' && (
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="text-xs text-muted-foreground bg-muted/30 border-b border-border">
                <tr><th className="text-left px-3 py-2">Code</th><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Phone</th><th className="text-left px-3 py-2">Status</th></tr>
              </thead>
              <tbody>
                {drivers.map((d) => (
                  <tr key={d.id} className="border-b border-border last:border-b-0">
                    <td className="px-3 py-2 font-mono text-xs">{d.code}</td>
                    <td className="px-3 py-2">{d.full_name_en}</td>
                    <td className="px-3 py-2 text-xs font-mono">{d.phone}</td>
                    <td className="px-3 py-2 text-xs">{d.status}</td>
                  </tr>
                ))}
                {drivers.length === 0 && <tr><td colSpan={4} className="text-center text-sm text-muted-foreground py-4">{t('projects.noDrivers', { defaultValue: 'No drivers in this project.' })}</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function Card({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm mt-1">{value}</div>
    </div>
  );
}
